import React from "react";
import {
  Box,
  Typography,
  Paper,
  Modal,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VerifiedIcon from "@mui/icons-material/Verified";
import AssetOwnerImage from "./AssetOwnerImage";

export default function OwnershipCertificateModal({ open, onClose, data }) {
  if (!data) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          width: 550,
          maxHeight: "90vh",
          overflowY: "auto",
          p: 3,
          borderRadius: 3,
          mx: "auto",
          mt: 5,
          outline: "none",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Official Ownership Certificate
          </Typography>

          <CloseIcon
            onClick={onClose}
            sx={{ cursor: "pointer", color: "#475569" }}
          />
        </Stack>

        <Typography sx={{ color: "#6b7280", mb: 2 }}>
          {data.authority} verified digital certificate
        </Typography>

        {/* Image */}
        <Box
          sx={{
            height: 180,
            borderRadius: 3,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <AssetOwnerImage
            src={data.imageUrl}
            alt={data.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {data.title}
        </Typography>

        <Chip
          label={data.category}
          size="small"
          sx={{ bgcolor: "#e0f2fe", color: "#0284c7", mt: 1 }}
        />

        {/* Certificate Details */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#f8fafc",
          }}
        >
          <Stack spacing={1}>
            <DetailRow label="Certificate ID" value={data.id} />
            <DetailRow label="Issue Date" value={data.issueDate} />
            <DetailRow label="Tokens Owned" value={data.tokens} />
            <DetailRow label="Ownership Percentage" value={data.ownership} />
          </Stack>
        </Box>

        {/* Blockchain Hash */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Blockchain Verification Hash
          </Typography>

          <Typography
            sx={{
              fontSize: 13,
              p: 1,
              bgcolor: "#f1f5f9",
              borderRadius: 2,
              wordBreak: "break-all",
            }}
          >
            {data.blockHash ||
              "0x9B8a7cd65eF43a2b1c80d96ef7a0b54d32ef1a0b9cb87de0f5e43b32d1e0f9ab"}
          </Typography>
        </Box>

        {/* Verified Banner */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            bgcolor: "#ecfdf3",
            border: "1px solid #bbf7d0",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <VerifiedIcon sx={{ color: "#16a34a" }} />
          <Typography sx={{ color: "#166534", fontWeight: 600 }}>
            Cryptographically Verified — Signed by {data.authority}
          </Typography>
        </Box>

        {/* Footer Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            sx={{
              bgcolor: "#0ea5e9",
              textTransform: "none",
              "&:hover": { bgcolor: "#0284c7" },
            }}
          >
            Download PDF
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<VerifiedIcon />}
            sx={{ textTransform: "none" }}
          >
            Verify on Blockchain
          </Button>
        </Stack>
      </Paper>
    </Modal>
  );
}

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ color: "#6b7280" }}>{label}</Typography>
    <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
  </Stack>
);
